/** Local Storage Utility Class */
export class LocalStorageUtil {
  /**
   * Sets an item in local storage.
   *
   * @template T
   * @param {string} key - The key under which the value is stored.
   * @param {T} value - The value to store.
   * @static
   */
  static setItem<T>(key: string, value: T): void {
    localStorage.setItem(key, JSON.stringify(value));
  }

  /**
   * Retrieves an item from local storage.
   *
   * @template T
   * @param {string} key - The key of the item to retrieve.
   * @returns {T | null} - The retrieved value or null if not found.
   * @static
   */
  static getItem<T>(key: string): T | null {
    const item = localStorage.getItem(key);
    return item ? (JSON.parse(item) as T) : null;
  }

  /**
   * Removes an item from local storage.
   *
   * @param {string} key - The key of the item to remove.
   * @static
   */
  static removeItem(key: string): void {
    localStorage.removeItem(key);
  }

  /**
   * Clears all items from local storage.
   *
   * @static
   */
  static clear(): void {
    localStorage.clear();
  }
}

/** Session Storage Utility Class */
export class SessionStorageUtil {
  /**
   * Sets an item in session storage.
   *
   * @template T
   * @param {string} key - The key under which the value is stored.
   * @param {T} value - The value to store.
   * @static
   */
  static setItem<T>(key: string, value: T): void {
    sessionStorage.setItem(key, JSON.stringify(value));
  }

  /**
   * Retrieves an item from session storage.
   *
   * @template T
   * @param {string} key - The key of the item to retrieve.
   * @returns {T | null} - The retrieved value or null if not found.
   * @static
   */
  static getItem<T>(key: string): T | null {
    const item = sessionStorage.getItem(key);
    return item ? (JSON.parse(item) as T) : null;
  }

  /**
   * Removes an item from session storage.
   *
   * @param {string} key - The key of the item to remove.
   * @static
   */
  static removeItem(key: string): void {
    sessionStorage.removeItem(key);
  }

  /**
   * Clears all items from session storage.
   *
   * @static
   */
  static clear(): void {
    sessionStorage.clear();
  }
}
